import React, { useState, useCallback, useEffect } from 'react';
import { httpClient } from '@/infrastructure/http';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, ModalShell } from '@/presentation/components/common';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
type DemoEstado = 'pendiente' | 'contactado' | 'descartado';

interface DemoRequest {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  nombreConsultorio: string;
  mensaje: string | null;
  ip: string | null;
  estado: DemoEstado;
  notasAdmin: string | null;
  revisadoPor: string | null;
  revisadoEn: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

// ---------------------------------------------------------------------------
// Constantes UI
// ---------------------------------------------------------------------------
const ESTADO_LABELS: Record<DemoEstado, string> = {
  pendiente: 'Pendiente',
  contactado: 'Contactado',
  descartado: 'Descartado',
};

const ESTADO_COLORS: Record<DemoEstado, string> = {
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
  contactado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
  descartado: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-400',
};

// ---------------------------------------------------------------------------
// Modal de detalle / edición
// ---------------------------------------------------------------------------
interface ModalProps {
  item: DemoRequest;
  onClose: () => void;
  onSave: (id: number, estado: DemoEstado, notasAdmin: string) => Promise<void>;
}

const DemoRequestModal: React.FC<ModalProps> = ({ item, onClose, onSave }) => {
  const [estado, setEstado] = useState<DemoEstado>(item.estado);
  const [notas, setNotas] = useState(item.notasAdmin ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = estado !== item.estado || notas !== (item.notasAdmin ?? '');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(item.id, estado, notas);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      size="lg"
      title={item.nombreConsultorio}
      subtitle={`${item.nombre} · ${item.email}`}
      footer={
        <>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            isLoading={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={isSaving} className="w-full sm:w-auto">
            Cancelar
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400 block mb-0.5">Teléfono</span>
            <a href={`tel:${item.telefono}`} className="text-teal-600 dark:text-teal-400 font-medium">
              {item.telefono}
            </a>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400 block mb-0.5">Email</span>
            <a href={`mailto:${item.email}`} className="text-teal-600 dark:text-teal-400 font-medium break-all">
              {item.email}
            </a>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400 block mb-0.5">Recibido</span>
            <span className="text-slate-700 dark:text-slate-300">
              {new Date(item.creadoEn).toLocaleString('es-AR')}
            </span>
          </div>
          {item.revisadoPor && (
            <div>
              <span className="text-slate-500 dark:text-slate-400 block mb-0.5">Revisado por</span>
              <span className="text-slate-700 dark:text-slate-300">{item.revisadoPor}</span>
              {item.revisadoEn && (
                <span className="text-xs text-slate-400 dark:text-slate-500 block">
                  {new Date(item.revisadoEn).toLocaleString('es-AR')}
                </span>
              )}
            </div>
          )}
        </div>

        {item.mensaje && (
          <div>
            <span className="text-sm text-slate-500 dark:text-slate-400 block mb-1">Mensaje</span>
            <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-gray-900/50 rounded-lg p-3 whitespace-pre-wrap">
              {item.mensaje}
            </p>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
            Estado
          </label>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as DemoEstado)}
            className="w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="pendiente">Pendiente</option>
            <option value="contactado">Contactado</option>
            <option value="descartado">Descartado</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
            Notas internas
          </label>
          <textarea
            rows={3}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Anotaciones del equipo interno..."
            maxLength={2000}
            className="w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
          />
        </div>
      </div>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
type EstadoFilter = DemoEstado | 'todos';

export const DemoRequestsPage: React.FC = () => {
  const { success, error: showError } = useToast();

  const [items, setItems] = useState<DemoRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>('pendiente');
  const [selected, setSelected] = useState<DemoRequest | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = estadoFilter !== 'todos' ? `?estado=${estadoFilter}` : '';
      const res = await httpClient.get<DemoRequest[]>(`/demo-requests${params}`);
      setItems(res.data);
      setSelected((prev) => {
        if (!prev) return null;
        return res.data.find((s) => s.id === prev.id) ?? null;
      });
    } catch {
      showError('Error al cargar solicitudes de demo');
    } finally {
      setIsLoading(false);
    }
  }, [estadoFilter, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (id: number, estado: DemoEstado, notasAdmin: string) => {
    try {
      await httpClient.patch(`/demo-requests/${id}`, { estado, notasAdmin });
      success('Solicitud actualizada');
      await load();
    } catch {
      showError('Error al actualizar la solicitud');
      throw new Error('update failed');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const filterTabs: { key: EstadoFilter; label: string }[] = [
    { key: 'pendiente', label: 'Pendientes' },
    { key: 'contactado', label: 'Contactados' },
    { key: 'descartado', label: 'Descartados' },
    { key: 'todos', label: 'Todos' },
  ];

  return (
    <div className="panel-page h-full flex flex-col overflow-hidden !pb-3">
      {/* Filtro de estado */}
      <div className="flex gap-1.5 mb-2 flex-wrap flex-shrink-0">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setEstadoFilter(tab.key)}
            className={`panel-chip px-2.5 py-1.5 transition-colors ${
              estadoFilter === tab.key
                ? 'bg-teal-600 text-white'
                : 'bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="flex-1 min-h-0 overflow-auto">
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-500 dark:text-slate-400 text-sm">
          Sin solicitudes en este estado.
        </div>
      ) : (
        <div className="panel-table-wrap">
          <p className="panel-table-scroll-hint">Deslizá horizontalmente para ver más columnas</p>
          <table className="min-w-[560px] w-full text-sm">
            <thead className="panel-thead text-slate-500 dark:text-slate-400 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Consultorio</th>
                <th className="px-4 py-3 text-left">Contacto</th>
                <th className="hidden sm:table-cell px-4 py-3 text-left">Teléfono</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="hidden md:table-cell px-4 py-3 text-left">Recibido</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                    {item.nombreConsultorio}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    <div>{item.nombre}</div>
                    <div className="text-xs text-slate-400">{item.email}</div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-slate-600 dark:text-slate-300">
                    {item.telefono}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[item.estado]}`}>
                      {ESTADO_LABELS[item.estado]}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {new Date(item.creadoEn).toLocaleDateString('es-AR')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSelected(item)}
                      className="btn btn-sm btn-secondary"
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Modal de detalle */}
      {selected && (
        <DemoRequestModal
          item={selected}
          onClose={() => setSelected(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
